import {Component, OnInit} from "@angular/core";
import {ListView, TypeComparaison, TypeFilter} from "@domain/common/list-view";
import {FraisMissionListeItem} from "@domain/admin/frais/indemnites/frais-mission-liste-item";
import {TranslateService} from "@ngx-translate/core";
import {Sorting} from "@domain/common/list-view/sorting";
import {IndemnitesFraisMissionsListItemComponent} from "@components/admin/frais/indemnites/indemnites-frais-missions/indemnites-frais-missions-list-item/indemnites-frais-missions-list-item.component";
import {Result} from "@domain/common/http/result";
import {Router} from "@angular/router";
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction, TypeAction} from "@share/component/floating-button/floating-button";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environments/environment";
import {TypeCodeErreur} from "@domain/common/http/result";
import {finalize} from "rxjs/operators";

/**
 * Onglet frais missions
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'data-test-id': 'indemnites-frais-missions'},
    templateUrl: './indemnites-frais-missions.component.html'
})
export class IndemnitesFraisMissionsComponent implements OnInit {
    /** Liste des frais de mission */
    listeFraisMissions: ListView<FraisMissionListeItem, IndemnitesFraisMissionsListItemComponent>;

    /** Liste des actions du bouton flottant */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /**
     * Constructeur
     */
    constructor(
        private translateService: TranslateService,
        private router: Router,
        private http: HttpClient
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Initialisation du bouton flottant de création
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-ajouter',
            libelle: 'global.actions.creer',
            doAction: () => this.createFraisMission()
        }]);

        //Initialisation de la liste
        this.listeFraisMissions = new ListView<FraisMissionListeItem, IndemnitesFraisMissionsListItemComponent>({
            uri: '/controller/IJ/listeFraisMissionsSearch',
            title: this.translateService.instant('admin.frais.indemnites.onglets.fraisMissions'),
            component: IndemnitesFraisMissionsListItemComponent,
            isInfiniteScroll: true,
            defaultOrder: 'libelleTerritoire',
            isFilter: true,
            isSearchBar: true,
            isFilterVisible: true,
            mapResult: (result: Result) => this.mapResult(result),
            listeFilters: [{
                clef: '*region.*pays.libelle,*region.*ville.libelle,*region.*zone.libelle,*region.*region.libelle,libelleSpecial',
                title: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.territoire'),
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'typeTerritoire',
                title: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.type'),
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                listeValues: [
                    {code: 'pays', libelle: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.types.pays')},
                    {code: 'ville', libelle: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.types.ville')},
                    {code: 'zone', libelle: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.types.zone')},
                    {code: 'territoire', libelle: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.types.territoire')}
                ],
                multiple: true
            }, {
                clef: 'hasTauxSpecial',
                title: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.tauxSpecial'),
                type: TypeFilter[TypeFilter.BOOLEAN],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
            }, {
                clef: 'idDomaine',
                title: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.indemnite'),
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                loading: true,
                listeValues: [],
                multiple: true
            }],
            extraOptions: {
                consultFraisMission: (item: FraisMissionListeItem) => this.consultFraisMission(item)
            }
        });

        //Nom de classe pour la persistance des filtres
        this.listeFraisMissions.className = 'IndemnitesFraisMissionsV10Component';

        //Activation de la persistance des filtres
        this.listeFraisMissions.isPersistFilters = true;

        //Colonnes triables
        this.listeFraisMissions.columns = [
            {key: 'libelleTerritoire', title: this.translateService.instant('admin.frais.indemnites.fraisMissions.liste.territoire')},
        ];

        //Tri par défaut
        this.listeFraisMissions.sorting = new Sorting(this.listeFraisMissions.columns, 'libelleTerritoire');

        //Chargement des valeurs du filtre indemnités
        this.fetchFilterValues();
    }

    /**
     * Mapping de la liste des frais de mission depuis la réponse backend
     */
    private mapResult(result: Result): any {
        const sourceListe = result?.data?.listeFraisMissions ?? result;
        const donneesBrutes = sourceListe?.listeResultats ?? [];
        const listeResultats = (donneesBrutes ?? []).map((item: Partial<FraisMissionListeItem>) => new FraisMissionListeItem(item));

        return {
            listeResultats,
            numPage: sourceListe?.numPage ?? 0,
            nbPagesTotal: sourceListe?.nbPagesTotal ?? 1,
            nbObjetsTotal: sourceListe?.nbObjetsTotal ?? listeResultats.length,
            nbObjetsParPage: sourceListe?.nbObjetsParPage ?? 25,
            nbObjetsDansPage: listeResultats.length
        };
    }

    /**
     * Consultation d'un frais de mission
     */
    private consultFraisMission(item: FraisMissionListeItem): void {
        if (item?.idTaux) {
            this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions', item.idTaux]);
        }
    }

    /**
     * Redirection vers l'écran de création d'un frais de mission
     */
    private createFraisMission(): void {
        this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions', 0]);
    }

    /**
     * Chargement des valeurs du filtre indemnités (domaines de prestation)
     */
    private fetchFilterValues(): void {
        const filterDomaine = this.listeFraisMissions.listeFilters.find(f => f.clef === 'idDomaine');

        if (!filterDomaine) {
            return;
        }

        this.http.post<Result>(`${environment.baseUrl}/controller/IJ/listePourcentagesDomaine`, null)
            .pipe(
                finalize(() => {
                    filterDomaine.loading = false;
                })
            )
            .subscribe(result => {
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    const listePourcentages = result?.data?.listePourcentages ?? [];

                    //Conversion en format FilterValue {code, libelle}
                    filterDomaine.listeValues = listePourcentages
                        .filter(p => p.idDomaine > 0)
                        .map(p => ({
                            code: String(p.idDomaine),
                            libelle: p.libelleDomaine
                        }));
                }
            });
    }
}
